import { useState, useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import ReportTypeSelector from './components/ReportTypeSelector';
import DateRangeFilter from './components/DateRangeFilter';
import MemberFilterPanel from './components/MemberFilterPanel';
import SummaryMetricsPanel from './components/SummaryMetricsPanel';
import ContributionDataTable from './components/ContributionDataTable';
import ExportPanel from './components/ExportPanel';
import ReportTemplateCard from './components/ReportTemplateCard';
import TemplateReportDialog from './components/TemplateReportDialog';
import {
  ReportType,
  DateRange,
  MemberFilter,
  ContributionRecord,
  ChartDataPoint,
  SummaryMetric,
  ExportFormat,
  ReportTemplate,
} from './types';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';
import {
  exportTableToCSV,
  exportTableToExcel,
  exportTableToPDF,
  type ExportTableColumn,
  type ExportTableOptions,
} from '../../utils/exportUtils';

const ContributionChart = lazy(() => import('./components/ContributionChart'));
const PieChartSection = lazy(() => import('./components/PieChartSection'));

const Reports = () => {
  const { user, userProfile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('daily');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [groupLogos, setGroupLogos] = useState<Record<string, string>>({}); // groupId -> logoUrl mapping

  const reportTypes: ReportType[] = [
    {
      id: 'daily',
      label: 'Daily Report',
      description: 'View contributions for each day',
      icon: 'Calendar',
    },
    {
      id: 'weekly',
      label: 'Weekly Report',
      description: 'Analyze weekly contribution patterns',
      icon: 'CalendarDays',
    },
    {
      id: 'monthly',
      label: 'Monthly Report',
      description: 'Review monthly contribution summaries',
      icon: 'CalendarRange',
    },
  ];

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  const [members, setMembers] = useState<MemberFilter[]>([]);
  const [summaryMetrics, setSummaryMetrics] = useState<SummaryMetric[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [contributionRecords, setContributionRecords] = useState<ContributionRecord[]>([]);

  // Quick template output dialog state
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [templateDialogTitle, setTemplateDialogTitle] = useState<string>('');
  const [templateDialogSubtitle, setTemplateDialogSubtitle] = useState<string>('');
  const [templateDialogColumns, setTemplateDialogColumns] = useState<ExportTableColumn[]>([]);
  const [templateDialogRows, setTemplateDialogRows] = useState<Array<Record<string, string | number | boolean | null | undefined>>>([]);
  const [templateExportOpts, setTemplateExportOpts] = useState<ExportTableOptions | null>(null);

  // Pie chart data
  const [membersChartData, setMembersChartData] = useState<Array<{ name: string; value: number }>>([]);
  const [lendingChartData, setLendingChartData] = useState<Array<{ name: string; value: number }>>([]);
  const [contributionsChartData, setContributionsChartData] = useState<Array<{ name: string; value: number }>>([]);

  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      if (!user || !userProfile) return;

      try {
        setLoading(true);

        const isSuperAdmin = userProfile.role === 'super_admin';
        const isGroupAdmin = userProfile.role === 'group_admin';

        // Get user's groups
        let groupIds: string[] = [];

        if (isSuperAdmin) {
          // Super admin: Get all active groups
          const { data: allGroups, error: allGroupsError } = await supabase
            .from('groups')
            .select('id')
            .eq('is_active', true);

          if (!allGroupsError && allGroups) {
            groupIds = allGroups.map((g: any) => g.id);
          }
        } else {
          // Everyone else (group_admin + member): include groups where the user is a member.
          // This is required for members to see reports (read-only) for their group.
          const { data: memberGroups, error: memberGroupsError } = await supabase
            .from('group_members')
            .select('group_id')
            .eq('user_id', user.id);

          if (!memberGroupsError && memberGroups) {
            groupIds = memberGroups.map((mg: any) => mg.group_id).filter(Boolean);
          }

          // Also include groups the user created (for older groups missing group_members rows).
          const { data: createdGroups, error: createdError } = await supabase
            .from('groups')
            .select('id')
            .eq('created_by', user.id)
            .eq('is_active', true);

          if (!createdError && createdGroups) {
            const createdGroupIds = createdGroups.map((g: any) => g.id);
            groupIds = [...new Set([...groupIds, ...createdGroupIds])];
          }
        }

        if (groupIds.length === 0) {
          console.warn('No groups found for user');
          // Set empty data
          setContributionRecords([]);
          setSummaryMetrics([]);
          setChartData([]);
          setMembers([]);
          setLoading(false);
          return;
        }

        // Use first group as current group for display
        setCurrentGroupId(groupIds[0]);

        // Fetch group data with creator information to get company logos
        // First try with join, fallback to separate queries if it fails
        let groupsData: any[] | null = null;
        let groupsDataError: any = null;

        try {
          const result = await supabase
            .from('groups')
            .select(`
              id,
              created_by,
              company_logo_url,
              user_profiles!groups_created_by_fkey (
                id,
                company_logo_url,
                company_name
              )
            `)
            .in('id', groupIds);

          groupsData = result.data;
          groupsDataError = result.error;
        } catch (err) {
          console.warn('[REPORTS] Join query failed, trying fallback:', err);
          groupsDataError = err;
        }

        // Fallback: fetch groups without join, then fetch creators separately
        if (groupsDataError || !groupsData) {
          console.log('[REPORTS] Fetching groups without join...');
          const { data: groupsWithoutJoin, error: groupsError } = await supabase
            .from('groups')
            .select('id, created_by, company_logo_url')
            .in('id', groupIds);

          if (!groupsError && groupsWithoutJoin) {
            groupsData = groupsWithoutJoin;

            // Fetch creator profiles separately
            const creatorIds = [...new Set(groupsWithoutJoin.map((g: any) => g.created_by).filter(Boolean))];
            if (creatorIds.length > 0) {
              const { data: creators } = await supabase
                .from('user_profiles')
                .select('id, company_logo_url, company_name')
                .in('id', creatorIds);

              // Merge creator data into groups
              if (creators) {
                const creatorMap = new Map(creators.map((c: any) => [c.id, c]));
                groupsData = groupsWithoutJoin.map((g: any) => ({
                  ...g,
                  user_profiles: creatorMap.get(g.created_by) || null
                }));
              }
            }
          }
        }

        // Build logo map: groupId -> logoUrl (priority: group.company_logo_url > creator.company_logo_url > default)
        const logoMap: Record<string, string> = {};
        if (groupsData && groupsData.length > 0) {
          console.log('[REPORTS] Processing', groupsData.length, 'groups for logos');
          groupsData.forEach((group: any) => {
            const groupLogo = group.company_logo_url;
            const creator = group.user_profiles;
            const creatorLogo = creator?.company_logo_url;

            console.log(`[REPORTS] Group "${group.id}": groupLogo=${groupLogo || 'none'}, creatorLogo=${creatorLogo || 'none'}`);

            // Helper function to convert storage path to public URL if needed
            const getPublicLogoUrl = (logoUrl: string | null | undefined): string | null => {
              if (!logoUrl || logoUrl.trim() === '') return null;

              // If it's already a full URL (starts with http/https), use it as is
              if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
                console.log(`[REPORTS] Using full URL logo: ${logoUrl}`);
                return logoUrl;
              }

              // If it's a storage path (contains 'company-logos'), convert to public URL
              if (logoUrl.includes('company-logos')) {
                // Extract the path after 'company-logos/'
                const pathMatch = logoUrl.match(/company-logos\/(.+)/);
                if (pathMatch) {
                  const filePath = pathMatch[1];
                  const { data } = supabase.storage.from('company-logos').getPublicUrl(filePath);
                  console.log(`[REPORTS] Converted storage path to public URL: ${data.publicUrl}`);
                  return data.publicUrl;
                }
              }

              // If it's a relative path starting with /, it's likely already correct
              if (logoUrl.startsWith('/')) {
                console.log(`[REPORTS] Using relative path logo: ${logoUrl}`);
                return logoUrl;
              }

              return null;
            };

            // Priority: group logo > creator logo > default
            let logoUrl: string;
            const groupLogoUrl = getPublicLogoUrl(groupLogo);
            const creatorLogoUrl = getPublicLogoUrl(creatorLogo);

            if (groupLogoUrl) {
              logoUrl = groupLogoUrl;
              console.log(`[REPORTS] ✓ Group "${group.id}": Using group logo: ${logoUrl}`);
            } else if (creatorLogoUrl) {
              logoUrl = creatorLogoUrl;
              console.log(`[REPORTS] ✓ Group "${group.id}": Using creator logo: ${logoUrl}`);
            } else {
              logoUrl = '/assets/images/ihslogo.png';
              console.log(`[REPORTS] ✗ Group "${group.id}": No company logo found, using default IHS logo`);
            }

            logoMap[group.id] = logoUrl;
          });
        } else {
          console.warn('[REPORTS] No groups data available for logo mapping');
        }
        console.log('[REPORTS] Final logo map:', logoMap);
        setGroupLogos(logoMap);

        // Fetch group members from all user's groups
        const { data: groupMembers, error: membersError } = await supabase
          .from('group_members')
          .select(`
            user_id,
            group_id,
            user_profiles (
              id,
              full_name
            )
          `)
          .in('group_id', groupIds);

        if (!membersError && groupMembers) {
          // Deduplicate members
          const memberMap = new Map<string, MemberFilter>();
          groupMembers.forEach((gm: any) => {
            if (gm.user_profiles && !memberMap.has(gm.user_id)) {
              memberMap.set(gm.user_id, {
                id: gm.user_id,
                name: gm.user_profiles.full_name || 'Unknown',
                selected: true
              });
            }
          });
          setMembers(Array.from(memberMap.values()));
        }

        // Fetch contributions within date range from all user's groups
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        endDate.setHours(23, 59, 59, 999);

        let contributionsQuery = supabase
          .from('contributions')
          .select(`
            id,
            member_id,
            amount,
            contribution_date,
            notes,
            status,
            recorded_by,
            group_id,
            member:user_profiles!member_id (
              id,
              full_name
            ),
            recorder:user_profiles!recorded_by (
              id,
              full_name
            ),
            groups!inner (
              id,
              name
            )
          `)
          .in('group_id', groupIds)
          .gte('contribution_date', startDate.toISOString())
          .lte('contribution_date', endDate.toISOString())
          .order('contribution_date', { ascending: false });

        const { data: contributions, error: contribError } = await contributionsQuery;

        let fallbackContributions: any[] | null = null;
        let groupsMap = new Map<string, string>();

        if (contribError) {
          console.error('Error fetching contributions:', contribError);
          // Try without group join if it fails
          const fallbackQuery = supabase
            .from('contributions')
            .select(`
              id,
              member_id,
              amount,
              contribution_date,
              notes,
              status,
              recorded_by,
              group_id,
              member:user_profiles!member_id (
                id,
                full_name
              ),
              recorder:user_profiles!recorded_by (
                id,
                full_name
              )
            `)
            .in('group_id', groupIds)
            .gte('contribution_date', startDate.toISOString())
            .lte('contribution_date', endDate.toISOString())
            .order('contribution_date', { ascending: false });

          const { data: fallbackData, error: fallbackError } = await fallbackQuery;

          if (fallbackError) {
            throw fallbackError;
          }

          fallbackContributions = fallbackData;

          // Fetch group names separately
          const { data: groupsData } = await supabase
            .from('groups')
            .select('id, name')
            .in('id', groupIds);

          groupsMap = new Map(groupsData?.map((g: any) => [g.id, g.name]) || []);

          // Process contribution records with group names
          const records: ContributionRecord[] = (fallbackContributions || []).map((contrib: any) => ({
            id: contrib.id,
            date: contrib.contribution_date.split('T')[0],
            groupName: groupsMap.get(contrib.group_id) || 'Unknown Group',
            memberName: contrib.member?.full_name || 'Unknown',
            amount: parseFloat(contrib.amount || 0),
            recordedBy: contrib.recorder?.full_name || 'Unknown',
            verificationStatus: contrib.status === 'approved' ? 'verified' : contrib.status === 'pending_approval' ? 'pending' : contrib.status === 'rejected' ? 'rejected' : 'verified',
            notes: contrib.notes || undefined
          }));

          setContributionRecords(records);
        } else {
          // Process contribution records with group names from join
          const records: ContributionRecord[] = (contributions || []).map((contrib: any) => ({
            id: contrib.id,
            date: contrib.contribution_date.split('T')[0],
            groupName: contrib.groups?.name || 'Unknown Group',
            memberName: contrib.member?.full_name || 'Unknown',
            amount: parseFloat(contrib.amount || 0),
            recordedBy: contrib.recorder?.full_name || 'Unknown',
            verificationStatus: contrib.status === 'approved' ? 'verified' : contrib.status === 'pending_approval' ? 'pending' : contrib.status === 'rejected' ? 'rejected' : 'verified',
            notes: contrib.notes || undefined
          }));

          setContributionRecords(records);
        }

        // Get the records that were just set (from either branch)
        let finalRecords: ContributionRecord[];
        if (contribError) {
          // Use fallback data with groupsMap
          finalRecords = (fallbackContributions || []).map((contrib: any) => ({
            id: contrib.id,
            date: contrib.contribution_date.split('T')[0],
            groupName: groupsMap.get(contrib.group_id) || 'Unknown Group',
            memberName: contrib.member?.full_name || 'Unknown',
            amount: parseFloat(contrib.amount || 0),
            recordedBy: contrib.recorder?.full_name || 'Unknown',
            verificationStatus: contrib.status === 'approved' ? 'verified' : contrib.status === 'pending_approval' ? 'pending' : contrib.status === 'rejected' ? 'rejected' : 'verified',
            notes: contrib.notes || undefined
          }));
        } else {
          // Use contributions with group join
          finalRecords = (contributions || []).map((contrib: any) => ({
            id: contrib.id,
            date: contrib.contribution_date.split('T')[0],
            groupName: contrib.groups?.name || 'Unknown Group',
            memberName: contrib.member?.full_name || 'Unknown',
            amount: parseFloat(contrib.amount || 0),
            recordedBy: contrib.recorder?.full_name || 'Unknown',
            verificationStatus: contrib.status === 'approved' ? 'verified' : contrib.status === 'pending_approval' ? 'pending' : contrib.status === 'rejected' ? 'rejected' : 'verified',
            notes: contrib.notes || undefined
          }));
        }

        // Calculate summary metrics (for both branches)
        const totalAmount = finalRecords.reduce((sum, r) => sum + r.amount, 0);
        const avgAmount = finalRecords.length > 0 ? totalAmount / finalRecords.length : 0;
        const pendingCount = finalRecords.filter(r => r.verificationStatus === 'pending').length;
        const uniqueMembers = new Set(finalRecords.map(r => r.memberName)).size;
        const totalMembersCount = members.length || 1;
        const participationRate = totalMembersCount > 0 ? (uniqueMembers / totalMembersCount) * 100 : 0;

        setSummaryMetrics([
          {
            label: 'Total Contributions',
            value: `${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VUV`,
            change: 0,
            trend: 'neutral',
            icon: 'DollarSign',
          },
          {
            label: 'Average Amount',
            value: `${avgAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VUV`,
            change: 0,
            trend: 'neutral',
            icon: 'TrendingUp',
          },
          {
            label: 'Participation Rate',
            value: `${participationRate.toFixed(1)}%`,
            change: 0,
            trend: 'neutral',
            icon: 'Users',
          },
          {
            label: 'Pending Verifications',
            value: pendingCount.toString(),
            change: 0,
            trend: 'neutral',
            icon: 'Clock',
          },
        ]);

        // Aggregate chart data by date
        const dateMap = new Map<string, { amount: number; memberCount: number }>();
        finalRecords.forEach(record => {
          const date = record.date;
          const existing = dateMap.get(date) || { amount: 0, memberCount: 0 };
          existing.amount += record.amount;
          if (!dateMap.has(date)) {
            existing.memberCount = 1;
          }
          dateMap.set(date, existing);
        });

        const chartDataPoints: ChartDataPoint[] = Array.from(dateMap.entries())
          .map(([date, data]) => ({
            date,
            amount: data.amount,
            memberCount: data.memberCount
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

        setChartData(chartDataPoints);

        // Prepare pie chart data for contributions by member
        const contributionsByMember = new Map<string, number>();
        finalRecords.forEach(record => {
          const current = contributionsByMember.get(record.memberName) || 0;
          contributionsByMember.set(record.memberName, current + record.amount);
        });

        const contributionsPieData = Array.from(contributionsByMember.entries())
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10); // Top 10 contributors

        setContributionsChartData(contributionsPieData);

        // Prepare pie chart data for members by group
        const uniqueMembersByGroup = new Map<string, Set<string>>();
        finalRecords.forEach(record => {
          const groupName = record.groupName || 'Unknown Group';
          if (!uniqueMembersByGroup.has(groupName)) {
            uniqueMembersByGroup.set(groupName, new Set());
          }
          uniqueMembersByGroup.get(groupName)!.add(record.memberName);
        });

        const membersPieData = Array.from(uniqueMembersByGroup.entries())
          .map(([name, memberSet]) => ({ name, value: memberSet.size }))
          .sort((a, b) => b.value - a.value);

        setMembersChartData(membersPieData);

        // Fetch lending/loan data for pie chart
        try {
          const { data: loans, error: loansError } = await supabase
            .from('loans')
            .select(`
              id,
              member_id,
              loan_amount,
              remaining_balance,
              status,
              group_id,
              member:user_profiles!member_id (
                id,
                full_name
              )
            `)
            .in('group_id', groupIds)
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endDate.toISOString());

          if (!loansError && loans && loans.length > 0) {
            // Group loans by status
            const loansByStatus = new Map<string, number>();
            loans.forEach((loan: any) => {
              const status = loan.status || 'active';
              const current = loansByStatus.get(status) || 0;
              loansByStatus.set(status, current + parseFloat(loan.loan_amount || 0));
            });

            const lendingPieData = Array.from(loansByStatus.entries())
              .map(([name, value]) => ({
                name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
                value
              }));

            setLendingChartData(lendingPieData);
          } else {
            setLendingChartData([]);
          }
        } catch (err) {
          console.error('Error fetching loans:', err);
          setLendingChartData([]);
        }

      } catch (error) {
        console.error('Error fetching reports data:', error);
        // Set empty data on error
        setContributionRecords([]);
        setSummaryMetrics([
          {
            label: 'Total Contributions',
            value: '0.00 VUV',
            change: 0,
            trend: 'neutral',
            icon: 'DollarSign',
          },
          {
            label: 'Average Amount',
            value: '0.00 VUV',
            change: 0,
            trend: 'neutral',
            icon: 'TrendingUp',
          },
          {
            label: 'Participation Rate',
            value: '0.0%',
            change: 0,
            trend: 'neutral',
            icon: 'Users',
          },
          {
            label: 'Pending Verifications',
            value: '0',
            change: 0,
            trend: 'neutral',
            icon: 'Clock',
          },
        ]);
        setChartData([]);
        setContributionsChartData([]);
        setMembersChartData([]);
        setLendingChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, userProfile, dateRange]);

  const exportFormats: ExportFormat[] = [
    { id: 'pdf', label: 'PDF', icon: 'FileText', extension: '.pdf' },
    { id: 'excel', label: 'Excel', icon: 'FileSpreadsheet', extension: '.xlsx' },
    { id: 'csv', label: 'CSV', icon: 'FileDown', extension: '.csv' },
  ];

  const reportTemplates: ReportTemplate[] = [
    {
      id: 'member-summary',
      name: 'Member Summary',
      description: 'Individual member contribution overview',
      icon: 'User',
      fields: ['memberName', 'totalAmount', 'contributionCount', 'averageAmount', 'lastContribution'],
    },
    {
      id: 'period-totals',
      name: 'Period Totals',
      description: 'Aggregated contributions by time period',
      icon: 'Calendar',
      fields: ['period', 'totalAmount', 'memberCount', 'averagePerMember', 'verificationRate'],
    },
    {
      id: 'audit-trail',
      name: 'Audit Trail',
      description: 'Complete transaction history with changes',
      icon: 'Shield',
      fields: ['date', 'memberName', 'amount', 'recordedBy', 'verificationStatus', 'notes', 'timestamp'],
    },
    {
      id: 'verification-report',
      name: 'Verification Report',
      description: 'Pending and completed verifications',
      icon: 'CheckCircle',
      fields: ['date', 'memberName', 'amount', 'verificationStatus', 'verifiedBy', 'verificationDate'],
    },
  ];

  const handleMemberToggle = (memberId: string) => {
    setMembers(prev =>
      prev.map(m => (m.id === memberId ? { ...m, selected: !m.selected } : m))
    );
  };

  const handleSelectAllMembers = () => {
    setMembers(prev => prev.map(m => ({ ...m, selected: true })));
  };

  const handleDeselectAllMembers = () => {
    setMembers(prev => prev.map(m => ({ ...m, selected: false })));
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleExport = async (format: string, template: string, fields: string[]) => {
    try {
      console.log('Export started:', { format, template, fields, recordsCount: contributionRecords.length });

      // Filter records based on selected members
      const filteredRecords = contributionRecords.filter(record => {
        const member = members.find(m => m.name === record.memberName);
        return !member || member.selected;
      });

      console.log('Filtered records:', filteredRecords.length);

      // Use current summaryMetrics or create default if empty
      const metricsToExport = summaryMetrics.length > 0 ? summaryMetrics : [
        {
          label: 'Total Contributions',
          value: '0.00 VUV',
          change: 0,
          trend: 'neutral' as const,
          icon: 'DollarSign',
        },
        {
          label: 'Average Amount',
          value: '0.00 VUV',
          change: 0,
          trend: 'neutral' as const,
          icon: 'TrendingUp',
        },
        {
          label: 'Participation Rate',
          value: '0.0%',
          change: 0,
          trend: 'neutral' as const,
          icon: 'Users',
        },
        {
          label: 'Pending Verifications',
          value: '0',
          change: 0,
          trend: 'neutral' as const,
          icon: 'Clock',
        },
      ];

      console.log('Exporting with:', {
        format,
        records: filteredRecords.length,
        metrics: metricsToExport.length,
        reportType: selectedReportType,
        dateRange
      });

      // Get unique group names from filtered records
      const uniqueGroupNames = [...new Set(filteredRecords.map(r => r.groupName).filter(Boolean))];

      // Get logo URL - use currentGroupId's logo, or default if none found
      const logoUrl = currentGroupId ? (groupLogos[currentGroupId] || '/assets/images/ihslogo.png') : '/assets/images/ihslogo.png';

      console.log('[REPORTS EXPORT] Current group ID:', currentGroupId);
      console.log('[REPORTS EXPORT] Available group logos:', Object.keys(groupLogos));
      console.log('[REPORTS EXPORT] Selected logo URL:', logoUrl);

      switch (format) {
        case 'csv':
          exportToCSV(filteredRecords, metricsToExport, selectedReportType, dateRange, uniqueGroupNames, logoUrl);
          break;
        case 'excel':
          await exportToExcel(filteredRecords, metricsToExport, selectedReportType, dateRange, uniqueGroupNames, logoUrl);
          break;
        case 'pdf':
          await exportToPDF(filteredRecords, metricsToExport, selectedReportType, dateRange, uniqueGroupNames, logoUrl);
          break;
        default:
          console.error('Unknown export format:', format);
          alert('Unknown export format. Please select a valid format.');
          return;
      }

      console.log('Export completed successfully');
    } catch (error: any) {
      console.error('Error exporting report:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      alert(`Failed to export report: ${error?.message || 'Unknown error'}. Please check the console for details.`);
    }
  };

  const handleGenerateTemplate = (templateId: string) => {
    // Filter records based on selected members
    const selectedMemberNames = new Set(members.filter(m => m.selected).map(m => m.name));
    const filtered = contributionRecords.filter(r => selectedMemberNames.size === 0 || selectedMemberNames.has(r.memberName));

    const subtitleParts: string[] = [];
    if (currentGroupId) subtitleParts.push('Scope: Your accessible group(s)');
    subtitleParts.push(`Date range: ${dateRange.startDate} to ${dateRange.endDate}`);
    const subtitle = subtitleParts.join(' • ');

    const filenameBase = `report_${templateId}_${dateRange.startDate}_to_${dateRange.endDate}`;

    // Get logo URL for template exports
    const logoUrl = currentGroupId ? (groupLogos[currentGroupId] || '/assets/images/ihslogo.png') : '/assets/images/ihslogo.png';

    const openDialog = (title: string, columns: ExportTableColumn[], rows: Array<Record<string, any>>) => {
      const opts: ExportTableOptions = {
        title,
        subtitle,
        filename: filenameBase,
        columns,
        rows,
        logoUrl,
      };
      setTemplateDialogTitle(title);
      setTemplateDialogSubtitle(subtitle);
      setTemplateDialogColumns(columns);
      setTemplateDialogRows(rows);
      setTemplateExportOpts(opts);
      setIsTemplateDialogOpen(true);
    };

    const formatVuv = (n: number) => `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VUV`;

    if (templateId === 'member-summary') {
      const byMember = new Map<string, { total: number; count: number; last: string }>();
      filtered.forEach(r => {
        const existing = byMember.get(r.memberName) || { total: 0, count: 0, last: r.date };
        existing.total += r.amount;
        existing.count += 1;
        if (r.date > existing.last) existing.last = r.date;
        byMember.set(r.memberName, existing);
      });

      const rows = Array.from(byMember.entries())
        .map(([memberName, v]) => ({
          memberName,
          totalAmountNumber: v.total,
          totalAmount: formatVuv(v.total),
          contributionCount: v.count,
          averageAmount: formatVuv(v.count ? v.total / v.count : 0),
          lastContribution: v.last,
        }))
        .sort((a, b) => (Number(b.totalAmountNumber) || 0) - (Number(a.totalAmountNumber) || 0))
        .map(({ totalAmountNumber, ...rest }) => rest);

      openDialog('Member Summary', [
        { key: 'memberName', label: 'Member Name' },
        { key: 'totalAmount', label: 'Total Amount' },
        { key: 'contributionCount', label: 'Contribution Count' },
        { key: 'averageAmount', label: 'Average Amount' },
        { key: 'lastContribution', label: 'Last Contribution' },
      ], rows);
      return;
    }

    if (templateId === 'period-totals') {
      const toWeekStart = (dateStr: string) => {
        const d = new Date(`${dateStr}T00:00:00`);
        const day = d.getDay(); // 0=Sun..6=Sat
        const diff = (day === 0 ? -6 : 1) - day; // shift to Monday
        d.setDate(d.getDate() + diff);
        return d.toISOString().split('T')[0];
      };

      const periodKey = (dateStr: string) => {
        if (selectedReportType === 'weekly') return toWeekStart(dateStr);
        if (selectedReportType === 'monthly') return dateStr.slice(0, 7); // YYYY-MM
        return dateStr; // daily
      };

      const byPeriod = new Map<string, { total: number; members: Set<string>; totalCount: number; verifiedCount: number }>();
      filtered.forEach(r => {
        const key = periodKey(r.date);
        const existing = byPeriod.get(key) || { total: 0, members: new Set<string>(), totalCount: 0, verifiedCount: 0 };
        existing.total += r.amount;
        existing.members.add(r.memberName);
        existing.totalCount += 1;
        if (r.verificationStatus === 'verified') existing.verifiedCount += 1;
        byPeriod.set(key, existing);
      });

      const rows = Array.from(byPeriod.entries())
        .map(([period, v]) => {
          const memberCount = v.members.size || 0;
          const avgPerMember = memberCount ? v.total / memberCount : 0;
          const verificationRate = v.totalCount ? (v.verifiedCount / v.totalCount) * 100 : 0;
          return {
            period,
            totalAmount: formatVuv(v.total),
            memberCount,
            averagePerMember: formatVuv(avgPerMember),
            verificationRate: `${verificationRate.toFixed(1)}%`,
          };
        })
        .sort((a, b) => String(a.period).localeCompare(String(b.period)));

      openDialog('Period Totals', [
        { key: 'period', label: selectedReportType === 'weekly' ? 'Week Starting' : selectedReportType === 'monthly' ? 'Month' : 'Date' },
        { key: 'totalAmount', label: 'Total Amount' },
        { key: 'memberCount', label: 'Member Count' },
        { key: 'averagePerMember', label: 'Average Per Member' },
        { key: 'verificationRate', label: 'Verification Rate' },
      ], rows);
      return;
    }

    if (templateId === 'audit-trail') {
      const rows = filtered.map(r => ({
        date: r.date,
        groupName: r.groupName,
        memberName: r.memberName,
        amount: formatVuv(r.amount),
        recordedBy: r.recordedBy,
        verificationStatus: r.verificationStatus,
        notes: r.notes || '',
      }));

      openDialog('Audit Trail', [
        { key: 'date', label: 'Date' },
        { key: 'groupName', label: 'Group' },
        { key: 'memberName', label: 'Member' },
        { key: 'amount', label: 'Amount' },
        { key: 'recordedBy', label: 'Recorded By' },
        { key: 'verificationStatus', label: 'Status' },
        { key: 'notes', label: 'Notes' },
      ], rows);
      return;
    }

    if (templateId === 'verification-report') {
      const rows = filtered
        .filter(r => r.verificationStatus === 'pending' || r.verificationStatus === 'verified' || r.verificationStatus === 'rejected')
        .map(r => ({
          date: r.date,
          groupName: r.groupName,
          memberName: r.memberName,
          amount: formatVuv(r.amount),
          verificationStatus: r.verificationStatus,
          verifiedBy: r.verificationStatus === 'pending' ? '' : r.recordedBy,
          verificationDate: r.verificationStatus === 'pending' ? '' : r.date,
        }));

      openDialog('Verification Report', [
        { key: 'date', label: 'Date' },
        { key: 'groupName', label: 'Group' },
        { key: 'memberName', label: 'Member' },
        { key: 'amount', label: 'Amount' },
        { key: 'verificationStatus', label: 'Status' },
        { key: 'verifiedBy', label: 'Verified By' },
        { key: 'verificationDate', label: 'Verification Date' },
      ], rows);
      return;
    }

    console.warn('Unknown template:', templateId);
  };

  // Listen for report type changes from sidebar
  useEffect(() => {
    const handleReportTypeChange = (event: CustomEvent) => {
      const type = event.detail.type;
      if (type === 'daily' || type === 'weekly' || type === 'monthly') {
        setSelectedReportType(type);
      }
    };

    // Also check URL hash on mount
    const hash = window.location.hash.replace('#', '');
    if (hash === 'daily' || hash === 'weekly' || hash === 'monthly') {
      setSelectedReportType(hash);
    }

    window.addEventListener('report-type-change', handleReportTypeChange as EventListener);
    return () => {
      window.removeEventListener('report-type-change', handleReportTypeChange as EventListener);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Reports - communitySaving</title>
        <meta name="description" content="View comprehensive contribution analytics and export detailed reports for your community group" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} isMobileMenuOpen={isMobileMenuOpen} />
        <Sidebar
          userRole={userProfile?.role === 'super_admin' || userProfile?.role === 'group_admin' ? 'admin' : 'member'}
          isSuperAdmin={userProfile?.role === 'super_admin'}
          activeReportType={selectedReportType}
        />

        <main className="lg:ml-64 min-h-screen">
          <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-foreground mb-2">Reports & Analytics</h1>
              <p className="text-muted-foreground">
                Comprehensive contribution analytics and export capabilities
              </p>
            </div>

            <div className="space-y-6">
              <SummaryMetricsPanel metrics={summaryMetrics} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <ReportTypeSelector
                    reportTypes={reportTypes}
                    selectedType={selectedReportType}
                    onTypeChange={setSelectedReportType}
                  />

                  <DateRangeFilter dateRange={dateRange} onDateRangeChange={setDateRange} />

                  <Suspense fallback={<div className="bg-card rounded-md border border-border p-6 h-80" />}>
                    <ContributionChart data={chartData} title="Contribution Trends" />
                  </Suspense>

                  {/* Pie Charts Section */}
                  <Suspense fallback={<div className="grid grid-cols-1 lg:grid-cols-3 gap-6"><div className="bg-card rounded-md border border-border p-6 h-80" /><div className="bg-card rounded-md border border-border p-6 h-80" /><div className="bg-card rounded-md border border-border p-6 h-80" /></div>}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <PieChartSection
                        title="Contributions by Member"
                        icon="PieChart"
                        data={contributionsChartData}
                      />
                      <PieChartSection
                        title="Members by Group"
                        icon="Users"
                        data={membersChartData}
                      />
                      <PieChartSection
                        title="Lending Distribution"
                        icon="TrendingUp"
                        data={lendingChartData}
                      />
                    </div>
                  </Suspense>

                  <ContributionDataTable
                    records={contributionRecords}
                    onSort={handleSort}
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>

                <div className="space-y-6">
                  <MemberFilterPanel
                    members={members}
                    onMemberToggle={handleMemberToggle}
                    onSelectAll={handleSelectAllMembers}
                    onDeselectAll={handleDeselectAllMembers}
                  />

                  <ExportPanel
                    formats={exportFormats}
                    templates={reportTemplates}
                    onExport={handleExport}
                  />
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Quick Report Templates</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {reportTemplates.map((template) => (
                    <ReportTemplateCard
                      key={template.id}
                      template={template}
                      onGenerate={handleGenerateTemplate}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <TemplateReportDialog
        isOpen={isTemplateDialogOpen}
        title={templateDialogTitle}
        subtitle={templateDialogSubtitle}
        columns={templateDialogColumns}
        rows={templateDialogRows}
        onClose={() => setIsTemplateDialogOpen(false)}
        onExportCSV={() => {
          if (!templateExportOpts) return;
          exportTableToCSV(templateExportOpts);
        }}
        onExportExcel={async () => {
          if (!templateExportOpts) return;
          await exportTableToExcel(templateExportOpts);
        }}
        onExportPDF={async () => {
          if (!templateExportOpts) return;
          await exportTableToPDF(templateExportOpts);
        }}
      />
    </>
  );
};

export default Reports;